import axios from 'axios'

import { Header } from './config.api'
import { attemptTokenRefresh } from './tokenRefresh'
import { AUTH_CODE_TOKEN_EXPIRED, getAuthErrorCode } from './authErrorCode'

const apiClient = axios.create()

apiClient.interceptors.request.use(async (config) => {
    const { headers } = await Header()
    config.headers = { ...config.headers, ...headers }
    return config
})

apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error?.config
        if (error?.response?.status === 401 && originalRequest && !originalRequest._retry) {
            // The proactive check in Header() thought the token was still
            // fine, but the backend disagrees (clock skew, buffer too
            // tight, etc). Try a real refresh (retries + cross-tab lock +
            // ssoSilent) and transparently re-issue this same request once
            // with the fresh token before giving up — the caller never sees
            // this first 401 at all if it recovers.
            originalRequest._retry = true
            try {
                await attemptTokenRefresh()
                const { headers } = await Header()
                originalRequest.headers = { ...originalRequest.headers, ...headers }
                return apiClient(originalRequest)
            } catch {
                // attemptTokenRefresh() already fired 'token-expired' internally.
                return Promise.reject(error)
            }
        }
        if (error?.response?.status === 401 && originalRequest?._retry) {
            const code = getAuthErrorCode(error)
            if (code && code !== AUTH_CODE_TOKEN_EXPIRED) {
                const authRetryCount = originalRequest._authRetryCount ?? 0
                if (authRetryCount < 1) {
                    originalRequest._authRetryCount = authRetryCount + 1
                    await new Promise((resolve) => setTimeout(resolve, 500))
                    return apiClient(originalRequest)
                }
                // Retries exhausted for a non-expiry failure. This still
                // isn't necessarily a dead session, but rejecting here means
                // the caller is stuck with no way to recover — worse than an
                // occasionally-unnecessary re-login prompt. Fall through to
                // the same session-expired handling as a confirmed
                // TOKEN_EXPIRED.
            }
            // Already retried once with a freshly refreshed token and still
            // 401 — genuinely not a token problem, don't loop forever.
            window.dispatchEvent(new CustomEvent('token-expired'))
        }
        return Promise.reject(error)
    }
)

export default apiClient
